import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { apiFetch, getApiHost } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

type Phase = 'checking' | 'success' | 'incomplete' | 'error';

/**
 * Landing page for the in-site embedded checkout's return_url. Confirms the
 * Checkout Session outcome via /api/stripe/session-status and reflects it.
 * Credit/tier granting happens in the Stripe webhook; this polls briefly so the
 * UI catches up (and refreshes the user so the new balance/tier shows).
 */
const CheckoutReturnPage: React.FC = () => {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const sessionId = params.get('session_id');
  const [phase, setPhase] = useState<Phase>(sessionId ? 'checking' : 'error');
  const [type, setType] = useState<string | null>(null);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    for (let i = 0; i < 15; i++) {
      try {
        const res = await apiFetch(`${getApiHost()}/api/stripe/session-status?session_id=${encodeURIComponent(sessionId)}`);
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.status === 'complete') {
          setType(data.type ?? null);
          setPhase('success');
          refreshUser?.();
          return;
        }
        if (res.ok && data.status === 'expired') { setPhase('incomplete'); return; }
      } catch { /* keep polling */ }
      await new Promise(r => setTimeout(r, 1500));
    }
    // Still open after ~22s — treat as not completed (user may have closed it).
    setPhase('incomplete');
  }, [sessionId, refreshUser]);

  useEffect(() => { poll(); }, [poll]);

  const isSub = type === 'subscription';

  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="bg-crypto-gray border border-gray-700 rounded-2xl p-8 max-w-sm w-full text-center">
          {phase === 'checking' && (
            <>
              <div className="animate-spin w-8 h-8 border-2 border-crypto-accent border-t-transparent rounded-full mx-auto mb-4" />
              <h1 className="text-lg font-semibold">Confirming your payment…</h1>
              <p className="text-gray-400 text-sm mt-2">This only takes a moment.</p>
            </>
          )}
          {phase === 'success' && (
            <>
              <div className="text-5xl mb-3">✅</div>
              <h1 className="text-xl font-bold mb-1">Payment complete</h1>
              <p className="text-gray-400 text-sm mb-6">
                {isSub ? 'Your subscription is active — enjoy full access.' : 'Your credits have been added to your account.'}
              </p>
              <Link
                to={isSub ? '/profile' : '/profile/credits'}
                className="inline-block bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark font-medium px-6 py-2.5 rounded-lg transition-colors"
              >
                {isSub ? 'Go to your account' : 'View your credits'}
              </Link>
            </>
          )}
          {phase === 'incomplete' && (
            <>
              <div className="text-5xl mb-3">🕓</div>
              <h1 className="text-xl font-bold mb-1">Payment not completed</h1>
              <p className="text-gray-400 text-sm mb-6">No charge was made. You can try again anytime.</p>
              <Link to="/profile/credits" className="inline-block bg-gray-700 hover:bg-gray-600 text-white font-medium px-6 py-2.5 rounded-lg transition-colors">
                Back to credits
              </Link>
            </>
          )}
          {phase === 'error' && (
            <>
              <div className="text-5xl mb-3">⚠️</div>
              <h1 className="text-xl font-bold mb-1">Something went wrong</h1>
              <p className="text-gray-400 text-sm mb-6">We couldn't read this checkout session.</p>
              <Link to="/profile/credits" className="inline-block bg-gray-700 hover:bg-gray-600 text-white font-medium px-6 py-2.5 rounded-lg transition-colors">
                Back to credits
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CheckoutReturnPage;
